import React, { memo } from 'react';
import PropTypes from 'prop-types';

export const COLORS = {
  primary: '#ff6969',
  dark: '#55565a',
  light: '#8b90a3',
  white: '#fff',
};

export const ICON_NAMES = {
  'arrow-left': 'arrow-left',
  globe: 'globe',
  calendar: 'calendar',
  user: 'user',
  clock: 'clock',
  location: 'location',
  checkmark: 'checkmark',
  'arrow-small-right': 'arrow-small-right',
  'arrow-small-left': 'arrow-small-left',
  description: 'description',
  search: 'search',
};

/**
 * Renders icon depending on given name
 * @param {string} name icon name
 * @param {string} color optional icon color
 * @returns {JSX.Element}
 */
export function renderIcon(name, color, size, width) {
  switch (name) {
    case 'search':
      return (
        <svg
          data-test="search"
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 14 14"
        >
          <path
            id="ic_search_24px"
            d="M13.006 11.805h-.632l-.224-.216a5.211 5.211 0 1 0-.56.56l.216.224v.632l4 3.994L17 15.807zm-4.8 0a3.6 3.6 0 1 1 3.6-3.6 3.6 3.6 0 0 1-3.606 3.6z"
            fill={COLORS[color]}
            transform="translate(-3 -3)"
          />
        </svg>
      );

    case 'arrow-left':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={14.605 * size}
          height={16.801 * size}
          fill="none"
          viewBox="0 0 14.605 16.801"
          data-test="arrow-left"
        >
          <g id="back" transform="translate(.243 -8.246)">
            <path
              id="Path_22"
              d="M7.711 24.348a.68.68 0 1 0 .962-.962L2.65 17.363h10.531a.68.68 0 1 0 0-1.36H2.65l6.023-6.024a.68.68 0 0 0-.962-.962L.527 16.2a.68.68 0 0 0 0 .962z"
              fill={COLORS[color]}
              data-name="Path 22"
              strokeWidth={width}
              stroke={COLORS[color]}
            />
          </g>
        </svg>
      );

    case 'globe':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          data-test="globe"
        >
          <path
            id="ic_public_24px"
            d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm-1 17.93A7.988 7.988 0 0 1 4 12a8.118 8.118 0 0 1 .21-1.79L9 15v1a2.006 2.006 0 0 0 2 2zm6.9-2.54A1.984 1.984 0 0 0 16 16h-1v-3a1 1 0 0 0-1-1H8v-2h2a1 1 0 0 0 1-1V7h2a2.006 2.006 0 0 0 2-2v-.41a7.982 7.982 0 0 1 2.9 12.8z"
            transform="translate(-2 -2)"
            fill={COLORS[color]}
          />
        </svg>
      );

    case 'calendar':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18.51"
          height="19.813"
          viewBox="0 0 18.51 19.813"
          fill="none"
          data-test="calendar"
        >
          <path
            id="calender_event"
            d="M-1046.673-159.437a.327.327 0 0 1-.328-.326v-17.607a.327.327 0 0 1 .328-.326h2.281v-.978a.325.325 0 0 1 .326-.326h2.282a.326.326 0 0 1 .326.326v.978h7.174v-.978a.325.325 0 0 1 .326-.326h2.282a.326.326 0 0 1 .327.326v.978h2.283a.326.326 0 0 1 .326.326v17.608a.326.326 0 0 1-.326.326zm.326-.653h16.953v-13.368h-16.953zm0-14.02h16.953v-2.934h-1.956v.978a.326.326 0 0 1-.327.326h-2.282a.325.325 0 0 1-.326-.326v-.978h-7.174v.978a.326.326 0 0 1-.326.326h-2.282a.325.325 0 0 1-.326-.326v-.978h-1.956zm12.715-3.261v.978h1.63v-1.956h-1.63zm-10.108 0v.978h1.63v-1.956h-1.63zm9.13 15.325h-9.456v-9.454h12.389v9.456zm0-.653h2.281v-2.282h-2.281zm-2.936 0h2.282v-2.282h-2.282zm-2.933 0h2.281v-2.282h-2.281zm-2.936 0h2.282v-2.282h-2.282zm8.8-2.934h2.281v-2.282h-2.281zm-2.936 0h2.282v-2.282h-2.282zm-2.933 0h2.281v-2.282h-2.281zm-2.936 0h2.282v-2.282h-2.282zm5.869-2.935h2.282v-2.282h-2.282zm-2.933 0h2.281v-2.282h-2.281zm-2.936 0h2.282v-2.282h-2.282z"
            data-name="calender event"
            transform="translate(1047.126 179.125)"
            fill={Object.keys(COLORS).includes(color) ? COLORS[color] : color}
          />
        </svg>
      );

    case 'user':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="22.29"
          height="16.5"
          viewBox="0 0 22.29 16.5"
          fill="none"
          data-test="user"
        >
          <g id="user" transform="translate(.25 -27.25)">
            <g
              id="Group_1808"
              data-name="Group 1808"
              transform="translate(0 27.5)"
            >
              <path
                id="Path_2814"
                d="M156.158 39.671a1.843 1.843 0 0 0-.658 1.419v1.97a.434.434 0 1 0 .868 0v-1.97a.951.951 0 0 1 .342-.738 22.014 22.014 0 0 1 3.952-2.57.567.567 0 0 0 .316-.511v-2.776a.443.443 0 0 0-.193-.365.858.858 0 0 1-.377-.712v-2.876a2.14 2.14 0 0 1 2.123-2.152h.61a2.139 2.139 0 0 1 2.123 2.152v2.877a.858.858 0 0 1-.377.712.432.432 0 0 0-.193.365v2.77a.567.567 0 0 0 .316.511 21.855 21.855 0 0 1 3.952 2.57.965.965 0 0 1 .342.738v1.97a.434.434 0 1 0 .868 0v-1.97a1.85 1.85 0 0 0-.658-1.419 23.021 23.021 0 0 0-3.952-2.6V34.7a1.752 1.752 0 0 0 .57-1.294v-2.873a3.016 3.016 0 0 0-2.991-3.033h-.61a3.016 3.016 0 0 0-2.991 3.033v2.877a1.752 1.752 0 0 0 .57 1.294v2.366a23.3 23.3 0 0 0-3.952 2.601z"
                data-name="Path 2814"
                transform="translate(-148.382 -27.5)"
                fill={COLORS[color]}
              />
              <path
                id="Path_2815"
                d="M6.578 90.7h-.517a2.63 2.63 0 0 0-2.627 2.627v2.413a1.537 1.537 0 0 0 .485 1.121v1.9a19.618 19.618 0 0 0-3.329 2.156 1.612 1.612 0 0 0-.59 1.254v1.652a.453.453 0 0 0 .906 0v-1.652a.72.72 0 0 1 .261-.554 18.663 18.663 0 0 1 3.346-2.147.563.563 0 0 0 .316-.5v-2.328a.454.454 0 0 0-.2-.375.632.632 0 0 1-.284-.526v-2.414a1.725 1.725 0 0 1 1.721-1.721h.517A1.725 1.725 0 0 1 8.3 93.327v2.413a.632.632 0 0 1-.284.526.443.443 0 0 0-.2.375v1.909a.453.453 0 0 0 .906 0v-1.689a1.537 1.537 0 0 0 .488-1.121v-2.413A2.634 2.634 0 0 0 6.578 90.7z"
                data-name="Path 2815"
                transform="translate(0 -88.277)"
                fill={COLORS[color]}
              />
            </g>
          </g>
        </svg>
      );

    case 'clock':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          data-test="clock"
        >
          <g id="fast" transform="translate(-116.489 -58.245)">
            <g
              id="Group_1567"
              data-name="Group 1567"
              transform="translate(116.489 58.245)"
            >
              <g id="Group_1566" data-name="Group 1566">
                <path
                  id="Path_2812"
                  d="M124.489 58.245a8 8 0 1 0 8 8 8.009 8.009 0 0 0-8-8zm0 14.786a6.786 6.786 0 1 1 6.786-6.786 6.794 6.794 0 0 1-6.786 6.786z"
                  data-name="Path 2812"
                  transform="translate(-116.489 -58.245)"
                  fill={COLORS[color]}
                />
              </g>
            </g>
            <g
              id="Group_1569"
              data-name="Group 1569"
              transform="translate(123.554 62.467)"
            >
              <g id="Group_1568" data-name="Group 1568">
                <path
                  id="Path_2813"
                  d="M295.2 170.675v-4.118h-1.27v4.792l3.286 2.239.718-1.054z"
                  data-name="Path 2813"
                  transform="translate(-293.93 -166.557)"
                  fill={COLORS[color]}
                />
              </g>
            </g>
          </g>
        </svg>
      );

    case 'location':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="11.711"
          height="16.689"
          viewBox="0 0 11.711 16.689"
          fill="none"
          data-test="location"
        >
          <g
            id="location_icon"
            data-name="location icon"
            transform="translate(-7.95 .25)"
          >
            <path
              id="Path_23"
              d="M22.031 12a1.474 1.474 0 1 0 1.469 1.474A1.476 1.476 0 0 0 22.031 12zm0 2.527a1.053 1.053 0 1 1 1.053-1.053 1.054 1.054 0 0 1-1.053 1.053z"
              fill={COLORS[color]}
              stroke={COLORS[color]}
              data-name="Path 23"
              transform="translate(-8.164 -8.157)"
            />
            <path
              id="Path_24"
              d="M17.768 1.641a5.6 5.6 0 0 0-7.924 0 6.382 6.382 0 0 0-.527 7.875L13.806 16l4.483-6.474a6.385 6.385 0 0 0-.521-7.885zm.046 7.543l-4.009 5.789-4.015-5.8a5.767 5.767 0 0 1 .466-7.121 5.019 5.019 0 0 1 7.1 0 5.769 5.769 0 0 1 .459 7.132z"
              fill={COLORS[color]}
              stroke={COLORS[color]}
              data-name="Path 24"
            />
          </g>
        </svg>
      );

    case 'video':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14.706"
          height="17.555"
          viewBox="0 0 14.706 17.555"
        >
          <g id="webcam" transform="translate(-42.506 .25)">
            <g
              id="Group_1546"
              data-name="Group 1546"
              transform="translate(42.756)"
            >
              <g id="Group_1545" data-name="Group 1545">
                <path
                  id="Path_300"
                  d="M56.962 7.1a7.1 7.1 0 1 0-8.225 7.014v.742l-2.4.481a.553.553 0 0 0-.444.541v.622a.552.552 0 0 0 .552.552h6.836a.552.552 0 0 0 .552-.552v-.621a.553.553 0 0 0-.444-.541l-2.4-.481v-.742A7.114 7.114 0 0 0 56.962 7.1zm-3.682 8.761a.018.018 0 0 1 .014.017v.622a.018.018 0 0 1-.018.018h-6.835a.018.018 0 0 1-.018-.018v-.621a.018.018 0 0 1 .014-.017l2.593-.519h1.656zm-4.009-1.053v-.63a7.145 7.145 0 0 0 1.174 0v.63zm.587-1.139a6.569 6.569 0 1 1 6.57-6.569 6.576 6.576 0 0 1-6.569 6.572z"
                  stroke={COLORS[color]}
                  fill={COLORS[color]}
                  strokeWidth={0.5}
                  data-name="Path 300"
                  transform="translate(-42.756)"
                />
              </g>
            </g>
            <g
              id="Group_1548"
              data-name="Group 1548"
              transform="translate(46.679 3.94)"
            >
              <g id="Group_1547" data-name="Group 1547">
                <path
                  id="Path_301"
                  d="M165.652 119.716a3.18 3.18 0 1 0 3.18 3.18 3.184 3.184 0 0 0-3.18-3.18zm0 5.816a2.635 2.635 0 1 1 2.635-2.635 2.638 2.638 0 0 1-2.635 2.635z"
                  stroke={COLORS[color]}
                  strokeWidth={0.5}
                  data-name="Path 301"
                  transform="translate(-162.472 -119.716)"
                />
              </g>
            </g>
            <g
              id="Group_1550"
              data-name="Group 1550"
              transform="translate(48.444 5.697)"
            >
              <g id="Group_1549" data-name="Group 1549">
                <path
                  id="Path_302"
                  d="M198.091 153.921a1.415 1.415 0 1 0 1.415 1.415 1.417 1.417 0 0 0-1.415-1.415zm0 2.448a1.033 1.033 0 1 1 1.033-1.033 1.034 1.034 0 0 1-1.033 1.033z"
                  stroke={COLORS[color]}
                  strokeWidth={0.5}
                  data-name="Path 302"
                  transform="translate(-196.676 -153.921)"
                />
              </g>
            </g>
            <g
              id="Group_1552"
              data-name="Group 1552"
              transform="translate(49.261 1.71)"
            >
              <g id="Group_1551" data-name="Group 1551">
                <path
                  id="Path_303"
                  d="M231.479 42.756a.6.6 0 1 0 .6.6.6.6 0 0 0-.6-.6zm0 .815a.216.216 0 1 1 .216-.216.217.217 0 0 1-.216.216z"
                  fill={COLORS[color]}
                  data-name="Path 303"
                  transform="translate(-230.881 -42.756)"
                />
              </g>
            </g>
          </g>
        </svg>
      );

    case 'checkmark':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="33.229"
          height="33.229"
          viewBox="0 0 33.229 33.229"
          fill="none"
          data-test="checkmark"
        >
          <g id="Check_Icon" data-name="Check Icon">
            <path id="Bounds" d="M0 0h33.229v33.229H0z" fill="none" />
            <path
              id="Icon"
              d="M11.153 20.276l-5.815-5.815L3.4 16.4l7.753 7.753L27.768 7.538 25.83 5.6z"
              fill={COLORS[color]}
              transform="translate(1.307 2.153)"
            />
          </g>
        </svg>
      );

    case 'description':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="10.684"
          viewBox="0 0 14 10.684"
          data-test="description"
        >
          <g
            id="description_icon"
            data-name="description icon"
            transform="translate(-18.323 -758)"
          >
            <path
              id="Path_297"
              d="M61.9 155h12.853a.754.754 0 0 1 0 1.464H61.9a.754.754 0 0 1 0-1.464zm0 0"
              fill={COLORS[color]}
              data-name="Path 297"
              transform="translate(-43.001 607.537)"
            />
            <path
              id="Path_298"
              d="M61.841 91h6.865c.285 0 .517.328.517.732s-.232.732-.517.732h-6.865c-.285 0-.517-.328-.517-.732s.232-.732.517-.732zm0 0"
              fill={COLORS[color]}
              data-name="Path 298"
              transform="translate(-43.001 676.221)"
            />
            <path
              id="Path_299"
              d="M74.753 217a.754.754 0 0 1 0 1.464H61.9a.754.754 0 0 1 0-1.464zm0 0"
              fill={COLORS[color]}
              data-name="Path 299"
              transform="translate(-43.001 541)"
            />
          </g>
        </svg>
      );

    case 'arrow-small-right':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="7.229"
          height="11.689"
          viewBox="0 0 7.229 11.689"
          fill="none"
          data-test="arrow-small-right"
        >
          <path
            fill="none"
            stroke={COLORS[color]}
            strokeMiterlimit="10"
            strokeWidth="2"
            id="Path_23"
            d="M19.279 14.2l-5.172 5.107L9 14.2"
            data-name="Path 23"
            transform="rotate(-90 3.247 16.735)"
          />
        </svg>
      );

    case 'arrow-small-left':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="7.229"
          height="11.689"
          viewBox="0 0 7.229 11.689"
          fill="none"
          data-test="arrow-small-left"
        >
          <path
            fill="none"
            stroke={COLORS[color]}
            strokeMiterlimit="10"
            strokeWidth="2"
            id="Path_24"
            d="M19.279 14.2l-5.172 5.107L9 14.2"
            data-name="Path 24"
            transform="rotate(90 14.505 6.212)"
          />
        </svg>
      );

    case 'info':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 209.281 209.281"
        >
          <g>
            <path
              d="M104.641,72.862c5.812,0,10.541-4.723,10.541-10.528c0-5.826-4.729-10.566-10.541-10.566
    c-5.812,0-10.541,4.74-10.541,10.566C94.1,68.139,98.828,72.862,104.641,72.862z"
              fill={color || ''}
            />
            <path
              d="M123.349,142.412H112.14v-42.016c0-4.142-3.357-7.5-7.5-7.5H85.933c-4.142,0-7.5,3.358-7.5,7.5c0,4.142,3.358,7.5,7.5,7.5
    h11.208v42.016c0,4.142,3.358,7.5,7.5,7.5h18.709c4.143,0,7.5-3.358,7.5-7.5C130.849,145.77,127.492,142.412,123.349,142.412z"
              fill={color || ''}
            />
            <path
              d="M104.641,0.001C46.941,0.001,0,46.942,0,104.64s46.941,104.64,104.641,104.64c57.699,0,104.64-46.941,104.64-104.64
		S162.339,0.001,104.641,0.001z M104.641,194.28C55.212,194.28,15,154.068,15,104.64s40.212-89.64,89.641-89.64
    s89.64,40.212,89.64,89.64S154.069,194.28,104.641,194.28z"
              fill={color || ''}
            />
          </g>
        </svg>
      );

    case 'smartphone':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 216.842 216.842"
          width={size}
          height={size}
        >
          <g>
            <path
              d="M167.524,0H49.317c-8.6,0-15.596,7.001-15.596,15.606v185.641c0,8.599,6.996,15.595,15.596,15.595h118.207
         c8.6,0,15.596-6.996,15.596-15.595V15.606C183.12,7.001,176.124,0,167.524,0z M48.722,59.188H168.12v92.432H48.722V59.188z
          M49.317,15h118.207c0.334,0,0.596,0.266,0.596,0.606v28.582H48.722V15.606C48.722,15.271,48.989,15,49.317,15z M167.524,201.842
         H49.317c-0.317,0-0.596-0.278-0.596-0.595v-34.628H168.12v34.628C168.12,201.575,167.853,201.842,167.524,201.842z"
              fill={color || '#474747'}
            />
            <path
              d="M108.424,170.209c-7.725,0-14.01,6.29-14.01,14.023c0,7.72,6.285,14.001,14.01,14.001c7.727,0,14.012-6.281,14.012-14.001
         C122.437,176.499,116.151,170.209,108.424,170.209z M108.424,190.232c-3.335,0-6.01-2.681-6.01-6.001
         c0-3.326,2.675-6.023,6.01-6.023c3.331,0,6.012,2.697,6.012,6.023C114.437,187.551,111.755,190.232,108.424,190.232z"
              fill={color || '#474747'}
            />
            <path
              d="M96.716,34.602h23.416c2.761,0,5-2.239,5-5c0-2.761-2.239-5-5-5H96.716c-2.761,0-5,2.239-5,5
         C91.716,32.363,93.955,34.602,96.716,34.602z"
              fill={color || '#474747'}
            />
          </g>
        </svg>
      );

    case 'phone':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 20.139 20.139"
        >
          <g>
            <path
              d="M17.573 43.156a2.536 2.536 0 0 0-2.246 1.344.84.84 0 0 0 1.387.949c.37-.54.537-.626.764-.615a10.821 10.821 0 0 1 3.878 2.84.869.869 0 0 1-.008.613 2.9 2.9 0 0 1-1.44 1.813 2.7 2.7 0 0 1-2.159-.073 23.744 23.744 0 0 1-7.789-5.071 23.74 23.74 0 0 1-5.06-7.781 2.7 2.7 0 0 1-.073-2.16 2.9 2.9 0 0 1 1.813-1.439.868.868 0 0 1 .612-.009 10.842 10.842 0 0 1 2.842 3.87c.012.236-.074.4-.614.772a.84.84 0 0 0 .948 1.387 2.534 2.534 0 0 0 1.344-2.248c-.073-1.342-2.684-4.9-3.931-5.355a2.54 2.54 0 0 0-1.735-.01 4.522 4.522 0 0 0-2.8 2.332 4.325 4.325 0 0 0 .044 3.494 25.411 25.411 0 0 0 5.432 8.336l.017.017a25.418 25.418 0 0 0 8.326 5.423 4.984 4.984 0 0 0 1.876.391 3.851 3.851 0 0 0 1.617-.347 4.522 4.522 0 0 0 2.332-2.805 2.541 2.541 0 0 0-.009-1.731c-.469-1.253-4.024-3.864-5.368-3.937z"
              transform="translate(-2.949 -31.836)"
              fill={color || '#474747'}
            />
          </g>
        </svg>
      );

    case 'fax':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
          width={size}
          height={size}
        >
          <g>
            <g>
              <rect
                x="125.585"
                y="188.377"
                width="144.906"
                height="19.321"
                stroke={color || ''}
                strokeWidth={15}
                fill={color || ''}
              />
            </g>
          </g>
          <g>
            <g>
              <rect
                x="125.585"
                y="256"
                width="193.208"
                height="19.321"
                stroke={color || ''}
                strokeWidth={15}
                fill={color || ''}
              />
            </g>
          </g>
          <g>
            <g>
              <rect
                x="125.585"
                y="323.623"
                width="193.208"
                height="19.321"
                stroke={color || ''}
                strokeWidth={15}
                fill={color || ''}
              />
            </g>
          </g>
          <g>
            <g>
              <path
                d="M464.887,33.811H49.528C22.882,33.811,0,54.297,0,80.958v173.783c0,26.66,21.67,48.354,48.302,48.354
           c10.915,0,21.736-3.774,28.981-9.899v174.125c0,5.335,5.533,10.868,10.868,10.868h338.113c5.335,0,8.453-5.533,8.453-10.868
           V293.196c9.66,6.125,18.066,9.899,28.981,9.899c26.632,0,48.302-21.693,48.302-48.354V80.958
           C512,54.297,491.533,33.811,464.887,33.811z M415.396,254.83v204.038H96.604V254.83v-96.641c0-5.326,5.571-8.453,10.939-8.453
           h299.33c5.368,0,8.524,3.127,8.524,8.453V254.83z M492.679,254.741c0,16.009-13,29.033-28.981,29.033
           s-28.981-12.986-28.981-28.943v-96.641v-8.453h19.321v-19.321h-47.165h-299.33h-49.58v19.321h19.321v8.453v96.641
           c0,15.958-13,28.943-28.981,28.943c-15.981,0-28.981-13.024-28.981-29.033V80.958c0-16.009,14.217-27.826,30.208-27.826h415.359
           c15.991,0,27.792,11.816,27.792,27.826V254.741z"
                stroke={color || ''}
                strokeWidth={15}
                fill={color || ''}
              />
            </g>
          </g>
        </svg>
      );

    case 'mail':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 241.061 241.061"
          width={size}
          height={size}
        >
          <g>
            <path
              d="M198.602,70.402l-78.063,68.789l-78.08-68.79c-3.109-2.739-7.848-2.438-10.586,0.669c-2.737,3.108-2.439,7.847,0.67,10.586
		l83.039,73.159c1.417,1.248,3.188,1.872,4.958,1.872s3.542-0.624,4.959-1.873l83.022-73.159c3.107-2.738,3.406-7.478,0.668-10.586
		C206.449,67.964,201.711,67.664,198.602,70.402z"
              fill={color || ''}
            />
            <path
              d="M218.561,38.529H22.5c-12.406,0-22.5,10.093-22.5,22.5v119.002c0,12.407,10.094,22.5,22.5,22.5h196.061
		c12.406,0,22.5-10.093,22.5-22.5V61.029C241.061,48.623,230.967,38.529,218.561,38.529z M226.061,180.031
		c0,4.135-3.364,7.5-7.5,7.5H22.5c-4.136,0-7.5-3.365-7.5-7.5V61.029c0-4.135,3.364-7.5,7.5-7.5h196.061c4.136,0,7.5,3.365,7.5,7.5
		V180.031z"
              fill={color || ''}
            />
          </g>
        </svg>
      );

    case 'address':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 217.109 217.109"
          width={size}
          height={size}
        >
          <g>
            <path
              d="M108.552,0C62.42,0,24.89,37.535,24.89,83.672c0,32.967,13.802,66.314,39.914,96.437
		c19.503,22.499,38.831,35.246,39.645,35.778c1.246,0.815,2.675,1.222,4.104,1.222c1.428,0,2.857-0.407,4.104-1.222
		c0.813-0.532,20.144-13.279,39.647-35.777c26.114-30.124,39.917-63.471,39.917-96.438C192.22,37.535,154.686,0,108.552,0z
		 M108.552,200.4C93.165,188.934,39.89,144.806,39.89,83.672C39.89,45.806,70.691,15,108.552,15
		c37.864,0,68.668,30.806,68.668,68.672C177.22,144.806,123.94,188.934,108.552,200.4z"
              fill={color || ''}
            />
            <path
              d="M108.557,50.449c-18.31,0-33.206,14.897-33.206,33.209c0,18.307,14.896,33.201,33.206,33.201
		c18.31,0,33.205-14.894,33.205-33.201C141.762,65.347,126.866,50.449,108.557,50.449z M108.557,101.859
		c-10.039,0-18.206-8.165-18.206-18.201c0-10.041,8.167-18.209,18.206-18.209c10.038,0,18.205,8.168,18.205,18.209
		C126.762,93.694,118.595,101.859,108.557,101.859z"
              fill={color || ''}
            />
          </g>
        </svg>
      );

    case 'opening_time':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 209.28 209.28"
          width={size}
          height={size}
        >
          <g>
            <path
              d="M104.641,0C46.943,0,0.002,46.94,0.002,104.637c0,57.701,46.941,104.643,104.639,104.643
         c57.697,0,104.637-46.943,104.637-104.643C209.278,46.94,162.338,0,104.641,0z M104.641,194.28
         c-49.427,0-89.639-40.214-89.639-89.643C15.002,55.211,55.214,15,104.641,15c49.426,0,89.637,40.211,89.637,89.637
         C194.278,154.066,154.067,194.28,104.641,194.28z"
              fill={color || ''}
            />
            <path
              d="M158.445,102.886h-49.174V49.134c0-4.142-3.357-7.5-7.5-7.5c-4.142,0-7.5,3.358-7.5,7.5v61.252c0,4.142,3.358,7.5,7.5,7.5
         h56.674c4.143,0,7.5-3.358,7.5-7.5C165.945,106.244,162.587,102.886,158.445,102.886z"
              fill={color || ''}
            />
          </g>
        </svg>
      );

    case 'web':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 209.28 209.28"
          width={size}
          height={size}
        >
          <path
            d="M104.64,0C46.941,0,0,46.941,0,104.641c0,57.699,46.941,104.64,104.64,104.64s104.64-46.941,104.64-104.64
       C209.28,46.941,162.339,0,104.64,0z M104.64,194.28c-3.985,0-10.41-7.212-15.781-23.323c-2.508-7.524-4.53-16.142-6.028-25.501
       c7.052-0.56,14.366-0.856,21.809-0.856s14.757,0.296,21.809,0.857c-1.498,9.359-3.52,17.976-6.028,25.5
       C115.05,187.068,108.625,194.28,104.64,194.28z M104.64,129.6c-8.112,0-16.045,0.326-23.684,0.949
       c-0.783-8.309-1.196-17.004-1.196-25.909c0-8.905,0.413-17.6,1.196-25.91c7.638,0.623,15.571,0.949,23.681,0.949
       c8.113,0,16.047-0.326,23.687-0.949c0.783,8.31,1.196,17.005,1.196,25.91c0,8.905-0.413,17.6-1.196,25.909
       C120.685,129.927,112.751,129.6,104.64,129.6z M15,104.641c0-13.89,3.176-27.051,8.838-38.797
       c11.079,5.063,25.643,8.91,42.221,11.246c-0.863,9.004-1.3,18.287-1.3,27.551c0,9.264,0.437,18.547,1.3,27.551
       c-16.579,2.335-31.142,6.183-42.221,11.247C18.176,131.691,15,118.53,15,104.641z M104.64,15c3.985,0,10.41,7.212,15.781,23.323
       c2.508,7.524,4.53,16.141,6.028,25.5c-7.053,0.561-14.367,0.857-21.812,0.857c-7.442,0-14.755-0.296-21.806-0.856
       c1.498-9.359,3.52-17.977,6.028-25.5C94.23,22.212,100.655,15,104.64,15z M143.22,77.088c16.579-2.336,31.142-6.184,42.22-11.248
       c5.664,11.747,8.84,24.91,8.84,38.8c0,13.889-3.176,27.051-8.838,38.797c-11.079-5.063-25.643-8.91-42.221-11.246
       c0.863-9.003,1.3-18.287,1.3-27.551C144.52,95.376,144.083,86.092,143.22,77.088z M177.765,52.848
       c-9.493,4.108-22.095,7.307-36.409,9.337c-2.714-17.36-7.159-32.878-13.279-44.071C148.398,23.625,165.879,36.115,177.765,52.848z
        M81.204,18.115c-6.12,11.193-10.565,26.711-13.279,44.072c-14.314-2.03-26.917-5.229-36.411-9.336
       C43.4,36.116,60.881,23.625,81.204,18.115z M31.515,156.431c9.493-4.108,22.096-7.307,36.41-9.337
       c2.714,17.36,7.159,32.878,13.279,44.071C60.881,185.655,43.4,173.165,31.515,156.431z M128.077,191.165
       c6.12-11.193,10.565-26.711,13.278-44.071c14.314,2.03,26.917,5.229,36.411,9.337C165.88,173.165,148.399,185.655,128.077,191.165z"
            fill={color || ''}
          />
        </svg>
      );

    case 'linkedin':
      return (
        <svg
          viewBox="-21 -35 682.66669 682"
          width={size}
          height={size}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="m77.613281-.667969c-46.929687 0-77.613281 30.816407-77.613281 71.320313 0 39.609375 29.769531 71.304687 75.8125 71.304687h.890625c47.847656 0 77.625-31.695312 77.625-71.304687-.894531-40.503906-29.777344-71.320313-76.714844-71.320313zm0 0"
            fill={color || ''}
          />
          <path
            d="m8.109375 198.3125h137.195313v412.757812h-137.195313zm0 0"
            fill={color || ''}
          />
          <path
            d="m482.054688 188.625c-74.011719 0-123.640626 69.546875-123.640626 69.546875v-59.859375h-137.199218v412.757812h137.191406v-230.5c0-12.339843.894531-24.660156 4.519531-33.484374 9.917969-24.640626 32.488281-50.167969 70.390625-50.167969 49.644532 0 69.5 37.851562 69.5 93.339843v220.8125h137.183594v-236.667968c0-126.78125-67.6875-185.777344-157.945312-185.777344zm0 0"
            fill={color || ''}
          />
        </svg>
      );

    case 'youtube':
      return (
        <svg
          viewBox="-21 -117 682.66672 682"
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
        >
          <path
            d="m626.8125 64.035156c-7.375-27.417968-28.992188-49.03125-56.40625-56.414062-50.082031-13.703125-250.414062-13.703125-250.414062-13.703125s-200.324219 0-250.40625 13.183593c-26.886719 7.375-49.03125 29.519532-56.40625 56.933594-13.179688 50.078125-13.179688 153.933594-13.179688 153.933594s0 104.378906 13.179688 153.933594c7.382812 27.414062 28.992187 49.027344 56.410156 56.410156 50.605468 13.707031 250.410156 13.707031 250.410156 13.707031s200.324219 0 250.40625-13.183593c27.417969-7.378907 49.03125-28.992188 56.414062-56.40625 13.175782-50.082032 13.175782-153.933594 13.175782-153.933594s.527344-104.382813-13.183594-154.460938zm-370.601562 249.878906v-191.890624l166.585937 95.945312zm0 0"
            fill={color || ''}
          />
        </svg>
      );

    case 'twitter':
      return (
        <svg
          viewBox="-21 -81 681.33464 681"
          width={size}
          height={size}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="m200.964844 515.292969c241.050781 0 372.871094-199.703125 372.871094-372.871094 0-5.671875-.117188-11.320313-.371094-16.9375 25.585937-18.5 47.824218-41.585937 65.371094-67.863281-23.480469 10.441406-48.753907 17.460937-75.257813 20.636718 27.054687-16.230468 47.828125-41.894531 57.625-72.488281-25.320313 15.011719-53.363281 25.917969-83.214844 31.808594-23.914062-25.472656-57.964843-41.402344-95.664062-41.402344-72.367188 0-131.058594 58.6875-131.058594 131.03125 0 10.289063 1.152344 20.289063 3.398437 29.882813-108.917968-5.480469-205.503906-57.625-270.132812-136.921875-11.25 19.363281-17.742188 41.863281-17.742188 65.871093 0 45.460938 23.136719 85.605469 58.316407 109.082032-21.5-.660156-41.695313-6.5625-59.351563-16.386719-.019531.550781-.019531 1.085937-.019531 1.671875 0 63.46875 45.171875 116.460938 105.144531 128.46875-11.015625 2.996094-22.605468 4.609375-34.558594 4.609375-8.429687 0-16.648437-.828125-24.632812-2.363281 16.683594 52.070312 65.066406 89.960937 122.425781 91.023437-44.855469 35.15625-101.359375 56.097657-162.769531 56.097657-10.5625 0-21.003906-.605469-31.2617188-1.816407 57.9999998 37.175781 126.8710938 58.871094 200.8867188 58.871094"
            fill={color || ''}
          />
        </svg>
      );

    case 'instagram':
      return (
        <svg
          viewBox="0 0 511 511.9"
          width={size}
          height={size}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="m510.949219 150.5c-1.199219-27.199219-5.597657-45.898438-11.898438-62.101562-6.5-17.199219-16.5-32.597657-29.601562-45.398438-12.800781-13-28.300781-23.101562-45.300781-29.5-16.296876-6.300781-34.898438-10.699219-62.097657-11.898438-27.402343-1.300781-36.101562-1.601562-105.601562-1.601562s-78.199219.300781-105.5 1.5c-27.199219 1.199219-45.898438 5.601562-62.097657 11.898438-17.203124 6.5-32.601562 16.5-45.402343 29.601562-13 12.800781-23.097657 28.300781-29.5 45.300781-6.300781 16.300781-10.699219 34.898438-11.898438 62.097657-1.300781 27.402343-1.601562 36.101562-1.601562 105.601562s.300781 78.199219 1.5 105.5c1.199219 27.199219 5.601562 45.898438 11.902343 62.101562 6.5 17.199219 16.597657 32.597657 29.597657 45.398438 12.800781 13 28.300781 23.101562 45.300781 29.5 16.300781 6.300781 34.898438 10.699219 62.101562 11.898438 27.296876 1.203124 36 1.5 105.5 1.5s78.199219-.296876 105.5-1.5c27.199219-1.199219 45.898438-5.597657 62.097657-11.898438 34.402343-13.300781 61.601562-40.5 74.902343-74.898438 6.296876-16.300781 10.699219-34.902343 11.898438-62.101562 1.199219-27.300781 1.5-36 1.5-105.5s-.101562-78.199219-1.300781-105.5zm-46.097657 209c-1.101562 25-5.300781 38.5-8.800781 47.5-8.601562 22.300781-26.300781 40-48.601562 48.601562-9 3.5-22.597657 7.699219-47.5 8.796876-27 1.203124-35.097657 1.5-103.398438 1.5s-76.5-.296876-103.402343-1.5c-25-1.097657-38.5-5.296876-47.5-8.796876-11.097657-4.101562-21.199219-10.601562-29.398438-19.101562-8.5-8.300781-15-18.300781-19.101562-29.398438-3.5-9-7.699219-22.601562-8.796876-47.5-1.203124-27-1.5-35.101562-1.5-103.402343s.296876-76.5 1.5-103.398438c1.097657-25 5.296876-38.5 8.796876-47.5 4.101562-11.101562 10.601562-21.199219 19.203124-29.402343 8.296876-8.5 18.296876-15 29.398438-19.097657 9-3.5 22.601562-7.699219 47.5-8.800781 27-1.199219 35.101562-1.5 103.398438-1.5 68.402343 0 76.5.300781 103.402343 1.5 25 1.101562 38.5 5.300781 47.5 8.800781 11.097657 4.097657 21.199219 10.597657 29.398438 19.097657 8.5 8.300781 15 18.300781 19.101562 29.402343 3.5 9 7.699219 22.597657 8.800781 47.5 1.199219 27 1.5 35.097657 1.5 103.398438s-.300781 76.300781-1.5 103.300781zm0 0"
            fill={color || ''}
          />
          <path
            d="m256.449219 124.5c-72.597657 0-131.5 58.898438-131.5 131.5s58.902343 131.5 131.5 131.5c72.601562 0 131.5-58.898438 131.5-131.5s-58.898438-131.5-131.5-131.5zm0 216.800781c-47.097657 0-85.300781-38.199219-85.300781-85.300781s38.203124-85.300781 85.300781-85.300781c47.101562 0 85.300781 38.199219 85.300781 85.300781s-38.199219 85.300781-85.300781 85.300781zm0 0"
            fill={color || ''}
          />
          <path
            d="m423.851562 119.300781c0 16.953125-13.746093 30.699219-30.703124 30.699219-16.953126 0-30.699219-13.746094-30.699219-30.699219 0-16.957031 13.746093-30.699219 30.699219-30.699219 16.957031 0 30.703124 13.742188 30.703124 30.699219zm0 0"
            fill={color || ''}
          />
        </svg>
      );

    case 'facebook':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={size * 0.54}
          height={size}
          viewBox="0 0 10.813 20"
        >
          <g>
            <path
              fill={color || '#474747'}
              d="M31.39 21.291h-2.485c-.391 0-.531-.141-.531-.534v-3.032c0-.389.148-.537.534-.537h2.482v-2.2a5.487 5.487 0 0 1 .682-2.815 4.145 4.145 0 0 1 2.228-1.856A5.519 5.519 0 0 1 36.218 10h2.459c.352 0 .509.156.509.511v2.852c0 .359-.15.5-.51.507-.672.007-1.344 0-2.015.03a.913.913 0 0 0-1.034 1.033c-.016.742-.007 1.485-.007 2.254h2.894c.41 0 .55.141.55.553v3.016c0 .406-.132.535-.544.535h-2.909v8.136c0 .434-.137.572-.564.572h-3.131c-.378 0-.526-.147-.526-.525v-8.183z"
              transform="translate(-28.374 -9.999)"
            />
          </g>
        </svg>
      );

    case 'message':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 237.02 237.02"
          width={size}
          height={size}
        >
          <g>
            <path
              d="M229.52,9.834H7.5c-4.142,0-7.5,3.358-7.5,7.5v153.85c0,4.142,3.358,7.5,7.5,7.5h55.492c4.142,0,7.5-3.358,7.5-7.5
		c0-4.142-3.358-7.5-7.5-7.5H15V24.834h207.02v138.85h-89.062c-1.527,0-3.018,0.466-4.273,1.337l-69.966,48.502
		c-3.404,2.359-4.25,7.032-1.891,10.437c1.456,2.101,3.793,3.228,6.171,3.228c1.474,0,2.963-0.433,4.266-1.337l68.038-47.166h94.216
		c4.142,0,7.5-3.358,7.5-7.5V17.334C237.02,13.192,233.662,9.834,229.52,9.834z"
              fill={color || ''}
            />
            <path
              d="M146.265,69.451c4.142,0,7.5-3.358,7.5-7.5s-3.358-7.5-7.5-7.5H90.759c-4.142,0-7.5,3.358-7.5,7.5s3.358,7.5,7.5,7.5
		H146.265z"
              fill={color || ''}
            />
            <path
              d="M55.507,94.26c0,4.142,3.358,7.5,7.5,7.5h111.01c4.142,0,7.5-3.358,7.5-7.5c0-4.142-3.358-7.5-7.5-7.5H63.007
		C58.865,86.76,55.507,90.117,55.507,94.26z"
              fill={color || ''}
            />
            <path
              d="M63.007,134.066h111.01c4.142,0,7.5-3.358,7.5-7.5c0-4.142-3.358-7.5-7.5-7.5H63.007c-4.142,0-7.5,3.358-7.5,7.5
		C55.507,130.708,58.865,134.066,63.007,134.066z"
              fill={color || ''}
            />
          </g>
        </svg>
      );

    case 'schedule':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 218.91 218.91"
          width={size}
          height={size}
        >
          <g>
            <path
              d="M211.41,17.713h-37.643V11.37c0-4.142-3.358-7.5-7.5-7.5s-7.5,3.358-7.5,7.5v6.344h-41.813V11.37
		c0-4.142-3.358-7.5-7.5-7.5c-4.142,0-7.5,3.358-7.5,7.5v6.344H60.141V11.37c0-4.142-3.358-7.5-7.5-7.5c-4.142,0-7.5,3.358-7.5,7.5
		v6.344H7.5c-4.142,0-7.5,3.358-7.5,7.5V207.54c0,4.142,3.358,7.5,7.5,7.5h203.91c4.142,0,7.5-3.358,7.5-7.5V25.213
		C218.91,21.071,215.552,17.713,211.41,17.713z M203.91,200.04H15V32.713h30.141v11.808c0,4.142,3.358,7.5,7.5,7.5
		c4.142,0,7.5-3.358,7.5-7.5V32.713h41.814v11.808c0,4.142,3.358,7.5,7.5,7.5c4.142,0,7.5-3.358,7.5-7.5V32.713h41.813v11.808
		c0,4.142,3.358,7.5,7.5,7.5s7.5-3.358,7.5-7.5V32.713h30.143V200.04z"
              fill={color || ''}
            />
            <path
              d="M169.889,130.96c3.313,0,6-2.687,6-6s-2.687-6-6-6h-16.877v-17.262c0-3.313-2.687-6-6-6c-3.313,0-6,2.687-6,6v17.262
		h-25.556v-17.262c0-3.313-2.686-6-6-6c-3.313,0-6,2.687-6,6v17.262H77.9v-17.262c0-3.313-2.686-6-6-6c-3.313,0-6,2.687-6,6v17.262
		H49.02c-3.313,0-6,2.687-6,6s2.687,6,6,6H65.9v18.721H49.02c-3.313,0-6,2.687-6,6c0,3.314,2.687,6,6,6H65.9v17.262
		c0,3.314,2.687,6,6,6c3.314,0,6-2.686,6-6V161.68h25.556v17.262c0,3.314,2.687,6,6,6c3.314,0,6-2.686,6-6V161.68h25.556v17.262
		c0,3.314,2.687,6,6,6c3.313,0,6-2.686,6-6V161.68h16.877c3.313,0,6-2.686,6-6c0-3.313-2.687-6-6-6h-16.877V130.96H169.889z
		 M77.9,149.68V130.96h25.556v18.721H77.9z M141.011,149.68h-25.556V130.96h25.556V149.68z"
              fill={color || ''}
            />
            <path
              d="M58.322,82.12h102.265c3.314,0,6-2.686,6-6c0-3.314-2.686-6-6-6H58.322c-3.314,0-6,2.686-6,6
		C52.322,79.434,55.008,82.12,58.322,82.12z"
              fill={color || ''}
            />
          </g>
        </svg>
      );

    case 'close':
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 18 18"
          fill={color || 'none'}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17.6007 0.41297C17.0683 -0.119454 16.2082 -0.119454 15.6758 0.41297L9 7.07509L2.32423 0.399317C1.79181 -0.133106 0.931741 -0.133106 0.399317 0.399317C-0.133106 0.931741 -0.133106 1.79181 0.399317 2.32423L7.07509 9L0.399317 15.6758C-0.133106 16.2082 -0.133106 17.0683 0.399317 17.6007C0.931741 18.1331 1.79181 18.1331 2.32423 17.6007L9 10.9249L15.6758 17.6007C16.2082 18.1331 17.0683 18.1331 17.6007 17.6007C18.1331 17.0683 18.1331 16.2082 17.6007 15.6758L10.9249 9L17.6007 2.32423C18.1195 1.80546 18.1195 0.931741 17.6007 0.41297Z"
            fill={color || '#E15757'}
          />
        </svg>
      );

    default:
      return <></>;
  }
}

const Icon = memo(({ name, color, size, width }) => {
  return renderIcon(name, color, size, width);
});

Icon.propTypes = {
  name: PropTypes.string.isRequired,
  color: PropTypes.string,
  size: PropTypes.number,
  width: PropTypes.number,
};

Icon.defaultProps = {
  color: 'light',
  size: 1,
  width: 1,
};

export default Icon;
